import React, { useContext, useState, useEffect, useReducer, useRef } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green } from "@material-ui/core/colors";
import {
  Button,
  Divider,
  Typography,
  IconButton,
  makeStyles
} from "@material-ui/core";

import {
  AccessTime,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  WhatsApp
} from "@material-ui/icons";
import LockIcon from '@material-ui/icons/Lock';
import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";
import PdfPreview from "../PdfPreview";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import AdMetaPreview from "../AdMetaPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import { CircularProgress } from "@material-ui/core";
import { useParams, useHistory } from 'react-router-dom';
import { downloadResource } from "../../utils";
import Template from "./templates";
import { usePdfViewer } from "../../hooks/usePdfViewer";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  currentTick: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    margin: "14px 0",
    pointerEvents: "none",
  },

  currentTicktText: {
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.80rem",
    padding: "6px 16px",
    borderRadius: "16px",
    boxShadow: theme.mode === "light" 
      ? "0 2px 8px rgba(0, 0, 0, 0.12)" 
      : "0 2px 8px rgba(0, 0, 0, 0.4)",
    letterSpacing: "0.3px",
    textAlign: "center",
    maxWidth: "85%",
    wordBreak: "break-word",
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 30px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: "5px dashed #333",
    color: 'inherit',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    fontFamily: "'Inter', 'Roboto', sans-serif !important",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    fontFamily: "'Inter', 'Roboto', sans-serif !important",
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    fontFamily: "'Inter', 'Roboto', sans-serif !important",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.mode === "light" ? "rgba(0, 0, 0, 0.45)" : "rgba(255, 255, 255, 0.70)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    // ✅ CORREÇÃO: objectFit removido para vídeos funcionarem melhor
    // objectFit: "cover", // Removido pois pode causar problemas em vídeos
    width: 400,
    height: "auto",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    // ✅ CORREÇÃO: Adicionar estilos específicos para vídeo
    "&[controls]": {
      objectFit: "contain", // Para vídeos, usar contain em vez de cover
    }
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.mode === 'light' ? "#808080" : "#d3d3d3",
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload;
    const newState = state.filter((m) => m.id !== messageId);
    return newState;
  }

  if (action.type === "RESET") {
    return [];
  }

  // [ANTIGRAVITY] PLAN 561: Return state by default to prevent undefined
  return state;
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  ticketStatus,
  isCTWA
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);

  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  // Hook simplificado para PDF
  const {
    downloadPdf,
    extractPdfInfoFromMessage,
    isPdfUrl
  } = usePdfViewer();

  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {
        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = ticketId;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", `${ticketId}`);
    }

    const onAppMessageMessagesList = (data) => {
      console.log(`📨 [Plan 561] Socket event received:`, {
        action: data.action,
        messageId: data.message?.id,
        mediaType: data.message?.mediaType,
        hasMediaUrl: !!data.message?.mediaUrl,
        ticketId: data.ticket?.id || data.message?.ticket?.id
      });

      // [ANTIGRAVITY] FIX: Support both UUID and Numeric ID matching
      const isSameTicket = (payloadTicket) => {
        if (!payloadTicket) return false;
        return payloadTicket.uuid === ticketId || payloadTicket.id == ticketId;
      };

      if (data.action === "create" && isSameTicket(data.ticket)) {
        console.log(`✅ [Plan 561] Adding message to UI:`, data.message.id);
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update" && isSameTicket(data?.message?.ticket)) {
        console.log(`🔄 [Plan 561] Updating message in UI:`, data.message.id);
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action === "delete" && isSameTicket(data.message.ticket)) {
        console.log(`🗑️ [Plan 561] Deleting message from UI:`, data.messageId);
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    }
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      socket.emit("joinChatBoxLeave", `${ticketId}`)
      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
    };

  }, [ticketId]);

  useEffect(() => {
    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    };
  }, [dragTimeout]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({});
      }
    }, 100);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const hanldeReplyMessage = (e, message) => {
    setAnchorEl(null);
    setReplyingMessage(message);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  const getMediaUrl = (message) => {
    if (!message.mediaUrl) return "";
    
    let relativeUrl = message.mediaUrl;
    
    // [ANTIGRAVITY] FIX: Eludir dominios cruzados locales y VPS (DB vs Env). Reconstruir con el Host API validado por Frontend.
    if (relativeUrl.startsWith("http")) {
      const publicIndex = relativeUrl.indexOf('/public');
      if (publicIndex !== -1) {
         return `${process.env.REACT_APP_BACKEND_URL}${relativeUrl.substring(publicIndex)}`;
      }
      return relativeUrl;
    }
    
    if (relativeUrl.startsWith("/public")) {
      return `${process.env.REACT_APP_BACKEND_URL}${relativeUrl}`;
    }

    return `${process.env.REACT_APP_BACKEND_URL}/public/${relativeUrl}`;
  };

  const checkMessageMedia = (message) => {
    const isVideoMessage = (message) => {
      if (message.mediaType === "video") return true;
      if (message.mediaUrl) {
        const videoExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.webm'];
        const url = message.mediaUrl.toLowerCase();
        if (videoExtensions.some(ext => url.includes(ext))) return true;
      }
      return false;
    };

    const isDocumentMessage = (message) => {
      const docExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.zip', '.rar'];
      if (message.mediaType === "document" || message.mediaType === "pdf" || message.mediaType === "word" || message.mediaType === "excel") return true;
      if (message.mediaUrl) {
        const url = message.mediaUrl.toLowerCase();
        if (docExtensions.some(ext => url.includes(ext))) return true;
      }
      return false;
    };

    
    const isImageMessage = (message) => {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
      if (message.mediaType === "image") return true;
      if (message.mediaUrl) {
        const url = message.mediaUrl.toLowerCase();
        if (imageExtensions.some(ext => url.includes(ext))) return true;
      }
      return false;
    };

    const isAudioMessage = (message) => {
      if (message.mediaType === "audio") {
        return true;
      }

      if (message.mediaUrl) {
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm'];
        const url = message.mediaUrl.toLowerCase();
        const hasAudioExtension = audioExtensions.some(ext => url.includes(ext));

        if (hasAudioExtension) {
          return true;
        }
      }

      if (message.body && typeof message.body === 'string') {
        const body = message.body.toLowerCase();
        const isAudioBody = body.includes('áudio gravado') ||
          body.includes('audio_') ||
          body.includes('🎵') ||
          body.includes('arquivo de áudio') ||
          body.includes('mensagem de voz');

        if (isAudioBody) {
          return true;
        }
      }

      return false;
    };

    // Templates
    if (message.mediaType === "template") {
      return <Template message={message} />;
    }

    // Localização
    else if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|');
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];
      let descriptionLocation = locationParts.length > 2 ? locationParts[2] : null;

      return <LocationPreview
        image={imageLocation}
        link={linkLocation}
        description={descriptionLocation}
      />;
    }

    // Contatos
    else if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";

      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }

      return <VcardPreview
        contact={contact}
        numbers={obj[0]?.number}
        queueId={message?.ticket?.queueId}
        whatsappId={message?.ticket?.whatsappId}
        channel={channel}
      />;
    }

    else if (message.mediaType === "adMetaPreview") { // Adicionado para renderizar o componente de preview de anúncio
      // ✅ CORREÇÃO: Parse correto dos dados - formato: image|sourceUrl|title|body|messageUser
      let [image, sourceUrl, title, body, messageUser] = message.body.split('|');

      // Fallback para messageUser se não estiver presente
      if (!messageUser || messageUser.trim() === "") {
        messageUser = "¡Hola! Tengo interés y quisiera más información, por favor.";
      }

      return <AdMetaPreview
        image={image}
        sourceUrl={sourceUrl}
        title={title}
        body={body}
        messageUser={messageUser}
      />;
    }

    // PDF e Documentos - SÓ DOWNLOAD
    else if (isDocumentMessage(message)) {
      const docInfo = extractPdfInfoFromMessage(message);

      // Ajustar tipo para o PdfPreview se necessário
      const isPdf = isPdfUrl(message.mediaUrl, message.body, message.mediaType);

      return (
        <PdfPreview
          url={getMediaUrl(message)}
          filename={docInfo.filename}
          size={docInfo.size}
          mediaType={docInfo.mediaType}
          onDownload={(url, name) => {
            downloadPdf(url, name);
          }}
        />
      );
    }

    // Áudio
    else if (isAudioMessage(message)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", minWidth: "250px" }}>
          <AudioModal
            url={getMediaUrl(message)}
            message={message}
          />
        </div>
      );
    }

    // Imagens
    else if (isImageMessage(message)) {
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <ModalImageCors imageUrl={getMediaUrl(message)} />
        </div>
      );
    }

    // Vídeos
    else if (isVideoMessage(message)) {
      return (
        <div style={{ maxWidth: "400px", width: "100%", position: "relative" }}>
          {/* Loading indicator */}
          {videoLoading && !videoError && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px"
            }}>
              <CircularProgress size={30} />
              <Typography variant="caption" color="textSecondary">
                {i18n.t("messagesList.header.buttons.videoLoading")}
              </Typography>
            </div>
          )}

          {/* Vídeo player melhorado */}
          <video
            className={classes.messageMedia}
            src={getMediaUrl(message)}
            controls
            preload="metadata"
            playsInline
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "300px",
              borderRadius: "8px",
              backgroundColor: "#f0f0f0",
              opacity: videoLoading ? 0.3 : 1,
              transition: "opacity 0.3s ease"
            }}
            onLoadStart={() => {
              setVideoLoading(true);
              setVideoError(false);
            }}
            onLoadedData={() => {
              setVideoLoading(false);
            }}
            onCanPlay={() => {
              setVideoLoading(false);
            }}
            onError={(e) => {
              setVideoLoading(false);
              setVideoError(true);
            }}
          >
            {/* ✅ CORREÇÃO: Múltiplos formatos para compatibilidade */}
            <source src={message.mediaUrl} type="video/mp4" />
            <source src={message.mediaUrl} type="video/webm" />
            <source src={message.mediaUrl} type="video/ogg" />

            {/* Fallback para navegadores antigos */}
            {i18n.t("messagesList.header.buttons.videoNotSupported")}
          </video>

          {/* Error state */}
          {videoError && (
            <div style={{
              padding: "20px",
              textAlign: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              color: "#666",
              marginTop: "8px"
            }}>
              <Typography variant="body2" style={{ marginBottom: "12px" }}>
                ❌ {i18n.t("messagesList.header.buttons.videoError")}
              </Typography>
              <Button
                startIcon={<GetApp />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = message.mediaUrl;
                  link.download = message.body || 'video.mp4';
                  link.click();
                }}
                variant="outlined"
                size="small"
              >
                {i18n.t("messagesList.header.buttons.downloadVideo")}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Outros tipos de arquivo
    else if (message.mediaUrl) {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Typography variant="body2" style={{ marginBottom: "8px", fontWeight: "bold" }}>
              {message.body || i18n.t("messagesList.header.buttons.file")}
            </Typography>
            <Button
              startIcon={<GetApp />}
              variant="outlined"
              onClick={() => downloadPdf(message.mediaUrl, message.body || 'archivo')}
            >
              {i18n.t("messagesList.header.buttons.download")}
            </Button>
          </div>
          <Divider />
        </>
      );
    }

    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    } else
      if (message.ack === 1) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      } else
        if (message.ack === 2 || message.ack === 3) {
          return <DoneAll fontSize="small" className={classes.ackIcons} />;
        } else
          if (message.ack === 4) {
            return <DoneAll fontSize="small" className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
          } else
            if (message.ack === 5) {
              return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
            }
  };

  const renderDailyTimestamps = (message, index) => {
    const today = format(new Date(), "dd/MM/yyyy");
    const isFirst = index === 0;
    const isLast = index === messagesList.length - 1;

    let isNewDay = false;
    if (isFirst) {
      isNewDay = true;
    } else if (index > 0 && messagesList[index - 1]) {
      const messageDay = parseISO(messagesList[index].createdAt);
      const previousMessageDay = parseISO(messagesList[index - 1].createdAt);
      if (!isSameDay(messageDay, previousMessageDay)) {
        isNewDay = true;
      }
    }

    const timestampElement = isNewDay ? (
      <span
        className={classes.dailyTimestamp}
        key={`timestamp-${message.id}`}
      >
        <div className={classes.dailyTimestampText}>
          {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")
            ? i18n.t("dates.today")
            : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
        </div>
      </span>
    ) : null;

    const lastRefElement = isLast ? (
      <div
        key={`ref-${message.id}`}
        ref={lastMessageRef}
        style={{ float: "left", clear: "both" }}
      />
    ) : null;

    if (timestampElement && lastRefElement) {
      return (
        <React.Fragment key={`daily-wrap-${message.id}`}>
          {timestampElement}
          {lastRefElement}
        </React.Fragment>
      );
    }

    return timestampElement || lastRefElement;
  };

  const renderTicketsSeparator = (message, index) => {
    let lastTicketMsg = messagesList[index - 1];
    let lastTicketId = lastTicketMsg?.ticketId;
    let currentTicketId = message.ticketId;

    if (index === 0) {
      return (
        <span className={classes.currentTick} key={`ticket-sep-start-${message.id}`}>
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: message?.ticket?.queue?.color || "#7c4dff" }} // Queue Color or Purple
          >
            {i18n.t("tickets.messagesList.separator.created")} #{currentTicketId} - {message?.ticket?.queue?.name || i18n.t("ticketsList.noQueue")}
          </div>
        </span>
      );
    }

    if (lastTicketId !== currentTicketId && lastTicketId !== undefined) {
      const isIgnored = lastTicketMsg?.ticket?.status === "ignored" || lastTicketMsg?.ticket?.motivoFinalizacao === "ignored";

      return (
        <div key={`ticket-sep-${message.id}`} style={{ width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* BARRA DE CIERRE/IGNORADO */}
          <span className={classes.currentTick}>
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: isIgnored ? "#4caf50" : "#f44336" }} // Green for Ignored, Red for Closed
            >
              {isIgnored ? i18n.t("tickets.messagesList.separator.ignored") : i18n.t("tickets.messagesList.separator.closed")} #{lastTicketId} - {lastTicketMsg?.ticket?.queue?.name || i18n.t("ticketsList.noQueue")}
            </div>
          </span>

          {/* BARRA DE APERTURA (MORADO/COLOR COLA) */}
          <span className={classes.currentTick}>
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: message?.ticket?.queue?.color || "#7c4dff" }} // Queue Color or Purple
            >
              {i18n.t("tickets.messagesList.separator.created")} #{currentTicketId} - {message?.ticket?.queue?.name || i18n.t("ticketsList.noQueue")}
            </div>
          </span>
        </div>
      );
    }

    // Si es el primer mensaje o no hay cambio, no pintamos separador (logica original)
    return null;
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;
      if (messageUser !== previousMessageUser) {
        return (

          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <AudioModal url={message.quotedMsg.mediaUrl} />
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <div style={{ maxWidth: "300px", width: "100%" }}>
                <video
                  className={classes.messageMedia}
                  src={message.quotedMsg.mediaUrl}
                  controls
                  preload="metadata"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "200px",
                    borderRadius: "6px",
                    backgroundColor: "#f0f0f0"
                  }}
                  onError={(e) => {
                    console.error("❌ Erro ao carregar vídeo citado:", e);
                  }}
                >
                  <source src={message.quotedMsg.mediaUrl} type="video/mp4" />
                  <source src={message.quotedMsg.mediaUrl} type="video/webm" />
                  <source src={message.quotedMsg.mediaUrl} type="video/ogg" />
                  <div style={{ padding: "10px", textAlign: "center", fontSize: "12px", color: "#999" }}>
                    ❌ {i18n.t("messagesList.header.buttons.videoError")}
                  </div>
                </video>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "contactMessage"
            && (
              i18n.t("messagesList.header.buttons.contact")
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  variant="outlined"
                  target="_blank"
                  href={message.quotedMsg.mediaUrl}
                >
                  {i18n.t("messagesList.header.buttons.download")}
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image"
            && (
              <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />)
            || message.quotedMsg?.body}

          {!message.quotedMsg.mediaType === "image" && message.quotedMsg?.body}

        </div>
      </div>
    );
  };

  const renderInteractiveButtons = (message) => {
    try {
      if (message.dataJson) {
        // Manejar tanto si es string como si ya viene parseado
        const data = typeof message.dataJson === 'string' ? JSON.parse(message.dataJson) : message.dataJson;
        
        // Soportar tanto formato directo como anidado, y formato Meta API Oficial (data.interactive)
        // Prioridad: data.interactive (Meta API) > data.message.interactive (anidado) > data directo (con action/type)
        const payload = data.interactive || data.message?.interactive || data;
        
        // [ANTIGRAVITY FIX] Soporte para renderizar Carruseles de Baileys en Botmix UI
        if (data.interactiveMessage?.carouselMessage) {
          const cards = data.interactiveMessage.carouselMessage.cards || [];
          return (
            <div style={{ display: 'flex', overflowX: 'auto', gap: '12px', marginTop: '12px', paddingBottom: '8px', maxWidth: '100%' }}>
              {cards.map((card, i) => {
                const headerTitle = card.header?.title || "";
                const bodyText = card.body?.text || "";
                const buttons = card.nativeFlowMessage?.buttons || [];
                const hasImage = card.header?.hasMediaAttachment;
                return (
                  <div key={i} style={{ 
                    minWidth: '240px', 
                    maxWidth: '260px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}>
                    {(() => {
                      const imgSrc = card.imageURL || card.header?._frontendImage || card.header?.imageMessage?.url || null;
                      if (imgSrc) {
                        return <img src={imgSrc} alt="Tarjeta" style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', backgroundColor: 'rgba(255, 255, 255, 0.05)' }} onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlOWVjZWYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiNhZGI1YmQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7wn5O3IEltYWdlbiBQcml2YWRhPC90ZXh0Pjwvc3ZnPg=='; }} />;
                      } else if (hasImage) {
                        return <div style={{ width: '100%', height: 130, backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: '12px' }}>📷 Imagen</div>;
                      }
                      return null;
                    })()}
                    <div style={{ padding: '12px', flex: 1 }}>
                      {headerTitle && <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '6px', color: 'inherit' }}>{headerTitle}</div>}
                      <div style={{ fontSize: '13px', color: 'inherit', whiteSpace: 'pre-wrap' }}>{bodyText}</div>
                    </div>
                    {buttons.map((btn, bIndex) => {
                      let btnText = "Botón";
                      try {
                        const btnParams = JSON.parse(btn.buttonParamsJson || "{}");
                        btnText = btnParams.display_text || btn.name;
                      } catch (e) {}
                      return (
                        <div key={bIndex} style={{ 
                          padding: '10px', 
                          borderTop: '1px solid #e0e0e0', 
                          textAlign: 'center', 
                          color: '#00a884', 
                          fontWeight: 'bold',
                          fontSize: '14px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }}>
                          {btnText}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        }
        
        if (payload && (payload.type === "button" || payload.type === "buttons" || payload.action?.buttons)) {
          const buttonsArray = payload.action?.buttons || [];
          const headerImage = payload.header?.image?.link || payload.header?.imageMessage?.url;
          const bodyText = payload.body?.text || message.body || "";
          
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              {headerImage && (
                <img src={headerImage} alt="Header" style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px' }} />
              )}
              {bodyText && (
                <div style={{ fontSize: '13px', color: 'inherit', whiteSpace: 'pre-wrap', padding: '4px 0' }}>{bodyText}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {buttonsArray.map((btn, i) => (
                  <div key={i} style={{ 
                    padding: '8px 16px', 
                    backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                    borderRadius: '10px', 
                    textAlign: 'center', 
                    color: '#00a884', 
                    fontWeight: 'bold',
                    fontSize: '14px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    cursor: 'pointer'
                  }}>
                    {btn.reply?.title || "Botón"}
                  </div>
                ))}
              </div>
            </div>
          );
        } else if (payload && payload.type === "list") {
          const buttonText = payload.action?.button || "Ver opciones";
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
              <div style={{ 
                padding: '8px 16px', 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '10px', 
                textAlign: 'center', 
                color: '#00a884', 
                fontWeight: 'bold',
                fontSize: '14px',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                <span style={{fontSize: "16px"}}>☰</span> {buttonText}
              </div>
            </div>
          );
        }
      }
    } catch (e) {
      // Ignorar errores silenciosamente
    }
    return null;
  };

  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      const hasFiles = event.dataTransfer &&
        event.dataTransfer.types &&
        (event.dataTransfer.types.includes('Files') ||
          event.dataTransfer.types.includes('application/x-moz-file'));

      if (hasFiles) {
        if (dragTimeout) clearTimeout(dragTimeout);
        const hasItems = event.dataTransfer.items && event.dataTransfer.items.length > 0;
        
        const timeout = setTimeout(() => {
          if (hasItems) {
            setDragActive(true);
          }
        }, 100);

        setDragTimeout(timeout);
      }
    } else if (event.type === "dragleave") {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  }

  const isYouTubeLink = (url) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();

    if (dragTimeout) {
      clearTimeout(dragTimeout);
      setDragTimeout(null);
    }

    setDragActive(false);

    if (event.dataTransfer.files &&
      event.dataTransfer.files.length > 0 &&
      event.dataTransfer.files[0] instanceof File) {
      if (onDrop) {
        onDrop(event.dataTransfer.files);
      }
    }
  }
  const xmlRegex = /<([^>]+)>/g;
  const boldRegex = /\*(.*?)\*/g;

  const formatXml = (xmlString) => {
    // Verifica se o XML contém a assinatura com nome do atendente
    if (boldRegex.test(xmlString)) {
      // Formata o texto dentro da assinatura em negrito
      xmlString = xmlString.replace(boldRegex, "**$1**");
    }
    return xmlString;
  };

  const formatMessageBody = (body) => {
    let cleanBody = body.replace(/^\[(?:Botones Interactivos|Lista Interactiva)\]\s*/i, "");
    if (cleanBody === "----- Implementaciones Apagadas -----") {
      return `----- ${i18n.t("messagesList.header.buttons.systemImplementationsOff") || "Implementaciones Apagadas"} -----`;
    }
    if (cleanBody === "----- Implementaciones Encendidas (Reinicio de Memoria IA) -----") {
      return `----- ${i18n.t("messagesList.header.buttons.systemImplementationsOn") || "Implementaciones Encendidas (Reinicio de Memoria IA)"} -----`;
    }
    return cleanBody;
  };

  const renderMessages = () => {

    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}

                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                    <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                  </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={classes.messageLeft}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>

                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> {i18n.t("messagesList.header.buttons.forwarded")}
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}

                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}
                    >🚫 {i18n.t("messagesList.header.buttons.deletedMessageByContact")} &nbsp;
                    </span>
                  </div>
                )}

                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview"
                ) && checkMessageMedia(message)}

                <div className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {
                    message.mediaType !== "adMetaPreview" && (
                      (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video" || message.mediaType === "audio") && !/^((ChatFile|Image|Video|Audio|Doc|file)_[a-f0-9_\-\.]{5,64}|[a-f0-9]{15,64}\.[a-z]{3,4})$/i.test(message.body) && !/\.(jpeg|jpg|png|gif|mp4|mp3|ogg|wav)$/i.test(message.body)) ||
                      (message.mediaUrl !== null && (message.mediaType === "application" || message.mediaType === "document") && message.body !== extractPdfInfoFromMessage(message).filename && !/^((ChatFile|Doc|file)_[a-f0-9_\-\.]{5,64}|[a-f0-9]{15,64}\.[a-z]{3,4})$/i.test(message.body)) ||
                      // [ANTIGRAVITY] PLAN 618: Si contiene espacios o es largo, es un caption humano.
                      (message.mediaUrl !== null && (message.body?.length > 64 || (message.body && message.body.includes(' ')))) ||
                      (message.mediaType !== "audio" &&
                        message.mediaType !== "image" &&
                        message.mediaType !== "video" &&
                        message.mediaType !== "application" &&
                        message.mediaType !== "document" &&
                        message.mediaType != "reactionMessage" &&
                        message.mediaType != "locationMessage" &&
                        message.mediaType !== "contactMessage" &&
                        message.mediaType !== "template" && message.mediaType !== "interative")
                    ) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <span>{message.body}</span>

                        )}
                        {!xmlRegex.test(message.body) && (
                          <MarkdownWrapper>{(lgpdDeleteMessage && message.isDeleted) ? "🚫 _" + i18n.t("messagesList.header.buttons.deletedMessage") + "_ " :
                            formatMessageBody(message.body)
                          }</MarkdownWrapper>)}

                      </>

                    )}

                  {renderInteractiveButtons(message)}

                  {message.quotedMsg && message.mediaType === "reactionMessage" && (
                    <>
                      <span style={{ marginLeft: "0px" }}>
                        <MarkdownWrapper>
                          {"" + message?.contact?.name + " " + i18n.t("messagesList.header.buttons.contactReacted") + " " + message.body}
                        </MarkdownWrapper>
                      </span>
                    </>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? i18n.t("messagesList.header.buttons.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={message.isPrivate ? classes.messageRightPrivate : classes.messageRight}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}

                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> {i18n.t("messagesList.header.buttons.forwarded")}
                    </span>
                    <br />
                  </div>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}
                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}
                    >🚫 {i18n.t("messagesList.header.buttons.deletedMessage")} &nbsp;
                    </span>
                  </div>
                )}
                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {
                    (
                      (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video" || message.mediaType === "audio") && !/^((ChatFile|Image|Video|Audio|Doc|file)_[a-f0-9_\-\.]{5,64}|[a-f0-9]{15,64}\.[a-z]{3,4})$/i.test(message.body) && !/^\d{10,15}(\.[a-z0-9]+)?$/i.test(message.body) && !/\.(jpeg|jpg|png|gif|mp4|mp3|ogg|wav)$/i.test(message.body)) ||
                      (message.mediaUrl !== null && (message.mediaType === "application" || message.mediaType === "document") && message.body !== extractPdfInfoFromMessage(message).filename && !/^((ChatFile|Doc|file)_[a-f0-9_\-\.]{5,64}|[a-f0-9]{15,64}\.[a-z]{3,4})$/i.test(message.body) && !/^\d{10,15}(\.[a-z0-9]+)?$/i.test(message.body)) ||
                      // [ANTIGRAVITY] PLAN 618: Si el texto tiene más de 64 caracteres o espacios, ES UN CAPTION humano.
                      (message.mediaUrl !== null && (message.body?.length > 64 || (message.body && message.body.includes(' ')))) ||
                      (message.mediaType !== "audio" &&
                        message.mediaType !== "image" &&
                        message.mediaType !== "video" &&
                        message.mediaType !== "application" &&
                        message.mediaType !== "document" &&
                        message.mediaType != "reactionMessage" &&
                        message.mediaType != "locationMessage" &&
                        message.mediaType !== "contactMessage" &&
                        message.mediaType !== "template" && message.mediaType !== "interative")
                    ) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <div>{formatXml(message.body)}</div>
                        )}
                        {!xmlRegex.test(message.body) && (<MarkdownWrapper>{formatMessageBody(message.body)}</MarkdownWrapper>)}
                      </>
                    )}

                  {renderInteractiveButtons(message)}

                  {message.quotedMsg && message.mediaType === "reactionMessage" && (
                    <>
                      <span style={{ marginLeft: "0px" }}>
                        <MarkdownWrapper>
                          {i18n.t("messagesList.header.buttons.youReacted") + " " + message.body}
                        </MarkdownWrapper>
                      </span>
                    </>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? i18n.t("messagesList.header.buttons.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });

      // [ANTIGRAVITY] FIX: Mostrar franja de cierre/ignorado para el último ticket de la lista
      const lastMessage = messagesList[messagesList.length - 1];
      if (lastMessage) {
        // Usamos ticketStatus (que viene del padre y es reactivo) para el ticket actual
        const isIgnored = ticketStatus === "ignored" || lastMessage.ticket?.status === "ignored" || lastMessage.ticket?.motivoFinalizacao === "ignored";
        const isClosed = ticketStatus === "closed" || lastMessage.ticket?.status === "closed";

        if (isIgnored || isClosed) {
          viewMessagesList.push(
            <span className={classes.currentTick} key={`ticket-sep-final-${lastMessage.id}`}>
              <div
                className={classes.currentTicktText}
                style={{ backgroundColor: isIgnored ? "#4caf50" : "#f44336" }} // Green for Ignored, Red for Closed
              >
                {isIgnored ? i18n.t("tickets.messagesList.separator.ignored") : i18n.t("tickets.messagesList.separator.closed")} #{lastMessage.ticketId} - {lastMessage.ticket?.queue?.name || i18n.t("ticketsList.noQueue")}
              </div>
            </span>
          );
        }
      }

      return viewMessagesList;
    } else {
      return <div>{i18n.t("messagesList.header.buttons.newContactGreeting")}</div>;
    }
  };
  const shouldBlurMessages = ticketStatus === "pending" && user.allowSeeMessagesInPendingTickets === "disabled";

  return (
    <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
      {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>{i18n.t("messagesList.header.buttons.dropFilesHere")}</div>}
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        isGroup={isGroup}
        whatsappId={whatsappId}
        queueId={queueId}
      />


      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
        style={{
          filter: shouldBlurMessages ? "blur(4px)" : "none",
          pointerEvents: shouldBlurMessages ? "none" : "auto"
        }}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>

      {["facebook", "instagram", "whatsapp_oficial", "webchat"].includes(channel) && (
        <div
          style={{
            width: "100%",
            display: "flex",
            padding: "10px",
            alignItems: "center",
            backgroundColor: "#E1F3FB",
            color: "#333",
          }}
        >
          {channel === "facebook" ? (
            <Facebook style={{ marginRight: 8 }} />
          ) : channel === "instagram" ? (
            <Instagram style={{ marginRight: 8 }} />
          ) : (
            <WhatsApp style={{ marginRight: 8 }} />
          )}

          <span>
            {isCTWA
              ? i18n.t("messagesList.header.buttons.metaPolicyWarning72h")
              : i18n.t("messagesList.header.buttons.metaPolicyWarning")}
          </span>
        </div>
      )}

      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
